<template>
  <div class="prod-dash">
    <!-- 헤더: 제목 + 범위 전환 -->
    <div class="dash-header">
      <span class="dash-title">작업현황</span>
      <el-button size="small" @click="helpVisible = true">도움말</el-button>
      <HelpPanel v-model="helpVisible" :content-path="'screens/production-dashboard'" />
      <el-radio-group v-model="scope" size="small" @change="fetchDashboard">
        <el-radio-button label="mine">내 담당</el-radio-button>
        <el-radio-button label="all">전체</el-radio-button>
      </el-radio-group>
    </div>

    <div v-loading="loading">
      <!-- 요약 칩 -->
      <div class="chips">
        <span class="chip chip-danger" :class="{ dim: !summary.delayed }">납기 지연 {{ summary.delayed || 0 }}</span>
        <span class="chip chip-warning" :class="{ dim: !summary.within_7days }">7일 내 납기 {{ summary.within_7days || 0 }}</span>
        <span class="chip chip-info">진행중 {{ summary.active || 0 }}</span>
      </div>

      <!-- 단계 필터 칩 -->
      <div class="stage-filter">
        <span
          class="stage-chip"
          :class="{ on: stageFilter === null }"
          @click="stageFilter = null"
        >전체 {{ orders.length }}</span>
        <span
          v-for="key in stageKeys"
          :key="key"
          class="stage-chip"
          :class="{ on: stageFilter === key }"
          @click="stageFilter = stageFilter === key ? null : key"
        >{{ stageLabels[key] }} {{ stageCount(key) }}</span>
      </div>

      <!-- 메인오더 진행 카드 -->
      <div v-if="filteredOrders.length" class="card-list">
        <div
          v-for="o in filteredOrders"
          :key="o.id"
          class="mr-card"
          :class="{ delayed: isDelayed(o) }"
          @click="$router.push(`/main-requests/${o.id}`)"
        >
          <div class="mr-head">
            <span class="mr-title">
              {{ o.request_number }}
              <span class="mr-sub">{{ o.brand_name }}<template v-if="o.design_style_number"> · {{ o.design_style_number }}</template></span>
            </span>
            <span class="dday" :class="ddayClass(o)">{{ ddayLabel(o) }}</span>
          </div>

          <!-- 5단계 진행바 -->
          <div class="stage-bar">
            <div
              v-for="(key, idx) in stageKeys"
              :key="key"
              class="seg"
              :class="segClass(o, idx)"
              :title="stageLabels[key]"
            ></div>
          </div>

          <div class="mr-next">
            <template v-if="o.next_milestone">
              {{ o.current_stage_label || stageLabels[o.current_stage] || '' }} 단계 ·
              다음: {{ o.next_milestone.name }}
              ({{ o.next_milestone.planned_date }} 예정<template v-if="o.next_milestone.is_overdue">,
                <span class="danger">{{ Math.abs(o.next_milestone.days_remaining) }}일 지남</span></template>)
            </template>
            <template v-else>
              {{ o.current_stage_label || '' }} 단계 ·
              마일스톤 {{ o.milestone_progress?.completed || 0 }}/{{ o.milestone_progress?.total || 0 }}
            </template>
          </div>

          <div class="mr-meta">
            <span v-if="o.manufacturer_name">{{ o.manufacturer_name }}</span>
            <span>{{ (o.order_quantity || 0).toLocaleString() }}장</span>
            <span v-if="o.material_secured_rate !== null && o.material_secured_rate !== undefined">자재 {{ o.material_secured_rate }}%</span>
          </div>
        </div>
      </div>

      <div v-else class="empty-line">
        {{ scope === 'mine'
          ? '내 담당 진행 건이 없습니다 — 우측 상단에서 "전체"로 전환해 보세요.'
          : '진행중인 메인오더가 없습니다 — 작업지시 입력 시 자동 표시됩니다.' }}
      </div>
    </div>
  </div>
</template>

<script setup>
import HelpPanel from '@/components/help/HelpPanel.vue'
import { ref as _helpRef } from 'vue'
const helpVisible = _helpRef(false)
import { ref, computed, onMounted } from 'vue'
import axiosInstance from '@/api/axios'
import { useUserStore } from '@/stores/user'

const userStore = useUserStore()

const loading = ref(false)
const summary = ref({})
const orders = ref([])
const stageFilter = ref(null)

// 기본 범위: 관리자(생산/메인요청 관리자, superuser 포함)는 전체, 그 외는 내 담당
const scope = ref(
  userStore.isProductionManager || userStore.isMainRequestManager ? 'all' : 'mine'
)

// 파이프라인 단계 (응답의 stage_labels 우선, 폴백 상수)
const FALLBACK_LABELS = {
  MATERIAL_PROCUREMENT: '자재조달',
  MATERIAL_EXPORT: '자재수출',
  PRODUCTION: '생산',
  PRODUCT_IMPORT: '제품수입',
  DELIVERY: '납품',
}
const stageLabels = computed(() => summary.value.stage_labels || FALLBACK_LABELS)
const stageKeys = computed(() => Object.keys(stageLabels.value))

const stageCount = (key) => summary.value.stage_distribution?.[key] || 0

const isDelayed = (o) =>
  (o.days_until_delivery !== null && o.days_until_delivery < 0) ||
  o.next_milestone?.is_overdue

const filteredOrders = computed(() => {
  let list = orders.value
  if (stageFilter.value) {
    list = list.filter((o) => o.current_stage === stageFilter.value)
  }
  // 지연 건 먼저, 그 다음 납기 가까운 순
  return [...list].sort((a, b) => {
    const da = isDelayed(a) ? 0 : 1
    const db = isDelayed(b) ? 0 : 1
    if (da !== db) return da - db
    return (a.days_until_delivery ?? 9999) - (b.days_until_delivery ?? 9999)
  })
})

const ddayLabel = (o) => {
  const d = o.days_until_delivery
  if (d === null || d === undefined) return '납기 미정'
  if (d < 0) return `D+${Math.abs(d)} 지연`
  return `납기 D-${d}`
}
const ddayClass = (o) => {
  const d = o.days_until_delivery
  if (d === null || d === undefined) return 'gray'
  if (d < 0) return 'red'
  if (d <= 7) return 'amber'
  return 'blue'
}

// 진행바 칸: 지난 단계 진파랑, 현재 단계 연파랑(지연 시 빨강), 이후 회색
const segClass = (o, idx) => {
  const cur = stageKeys.value.indexOf(o.current_stage)
  if (cur === -1) return 'todo'
  if (idx < cur) return 'done'
  if (idx === cur) return isDelayed(o) ? 'cur-delayed' : 'cur'
  return 'todo'
}

const fetchDashboard = async () => {
  loading.value = true
  try {
    const params = scope.value === 'mine' ? { scope: 'mine' } : {}
    const res = await axiosInstance.get('production/main-requests/dashboard/', { params })
    summary.value = res.data?.summary || {}
    orders.value = res.data?.orders || []
  } catch (err) {
    console.error('작업현황 조회 실패:', err)
  } finally {
    loading.value = false
  }
}

onMounted(fetchDashboard)
</script>

<style scoped>
.prod-dash {
  padding: 16px;
}

.dash-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 12px;
}
.dash-title {
  font-size: 16px;
  font-weight: 700;
}

/* ===== 요약 칩 ===== */
.chips {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}
.chip {
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 12px;
}
.chip-danger { background: #fef0f0; color: #c45656; }
.chip-warning { background: #fdf6ec; color: #b88230; }
.chip-info { background: #ecf5ff; color: #337ecc; }
.chip.dim { opacity: 0.5; }

/* ===== 단계 필터 ===== */
.stage-filter {
  display: flex;
  gap: 5px;
  overflow-x: auto;
  padding-bottom: 4px;
  margin-bottom: 10px;
  scrollbar-width: none;
}
.stage-filter::-webkit-scrollbar { display: none; }
.stage-chip {
  flex-shrink: 0;
  font-size: 12px;
  padding: 4px 11px;
  border-radius: 13px;
  border: 1px solid #dcdfe6;
  color: #606266;
  cursor: pointer;
  white-space: nowrap;
}
.stage-chip.on {
  background: #337ecc;
  border-color: #337ecc;
  color: #fff;
}

/* ===== 카드 리스트 ===== */
.card-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 10px;
}
.mr-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 10px;
  padding: 12px 14px;
  cursor: pointer;
}
.mr-card:hover { border-color: #c6e2ff; }
.mr-card.delayed { border-color: #f7c1c1; }

.mr-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}
.mr-title {
  font-size: 14px;
  font-weight: 700;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mr-sub {
  font-size: 12px;
  font-weight: 400;
  color: #909399;
}
.dday {
  flex-shrink: 0;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
}
.dday.red { background: #fef0f0; color: #c45656; }
.dday.amber { background: #fdf6ec; color: #b88230; }
.dday.blue { background: #ecf5ff; color: #337ecc; }
.dday.gray { background: #f5f7fa; color: #909399; }

.stage-bar {
  display: flex;
  gap: 3px;
  margin-bottom: 7px;
}
.seg {
  flex: 1;
  height: 5px;
  border-radius: 3px;
}
.seg.done { background: #378add; }
.seg.cur { background: #85b7eb; }
.seg.cur-delayed { background: #e24b4a; }
.seg.todo { background: #f0f2f5; }

.mr-next {
  font-size: 12px;
  color: #606266;
  line-height: 1.5;
}
.mr-meta {
  display: flex;
  gap: 10px;
  font-size: 11px;
  color: #c0c4cc;
  margin-top: 5px;
}

.danger { color: #f56c6c; }

.empty-line {
  border: 1px solid #ebeef5;
  border-radius: 10px;
  padding: 14px;
  font-size: 13px;
  color: #909399;
}

@media (max-width: 768px) {
  .prod-dash { padding: 12px; }
  .card-list { grid-template-columns: 1fr; }
}
</style>
